import React, { createContext, useContext, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  ChatModalType,
  ChatMode,
  ChatMessageFeedback,
  ChatStatus,
  type ChatMessageFeedbackState,
  type PatientConfirmationDetails,
  type ChatMessage,
  type SuggestionResponse,
} from '../lib/types';
import { ChatbotName } from '../../../../lib';
import {
  createChatSession,
  extractSuggestionResponse,
  sendChatMessage,
} from '../../../../api';

interface ChatContextValue {
  chatStatus: ChatStatus;
  modalType: ChatModalType;
  chatMode: ChatMode;
  conversationId: string | null;
  conversationStartedAt: string | null;
  caremapprAppUserId: string | null;
  onboardingInput: string;
  initialPrompt: string | null;
  preappointmentQuestionnaireSummary: string | null;
  chatStartedWithConsultId: boolean;
  patientConfirmationDetails: PatientConfirmationDetails | null;
  messages: ChatMessage[];
  isTyping: boolean;
  isStartingConversation: boolean;
  messageFeedback: Record<string, ChatMessageFeedbackState | undefined>;
  suggestedResponses: SuggestionResponse[];
  setModalType: (type: ChatModalType) => void;
  setChatStatus: (status: ChatStatus) => void;
  setChatMode: (mode: ChatMode) => void;
  setOnboardingInput: (value: string) => void;
  setInitialPrompt: (value: string | null) => void;
  setCaremapprAppUserId: (value: string | null) => void;
  setPreappointmentQuestionnaireSummary: (value: string | null) => void;
  setChatStartedWithConsultId: (value: boolean) => void;
  setPatientConfirmationDetails: (
    value: PatientConfirmationDetails | null,
  ) => void;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setIsTyping: (value: boolean) => void;
  setMessageFeedback: (
    messageId: string,
    feedback?: ChatMessageFeedback,
  ) => void;
  setSuggestedResponses: Dispatch<SetStateAction<SuggestionResponse[]>>;
  addMessage: (message: ChatMessage) => void;
  startConversation: () => Promise<string | null>;
  sendMessage: (message: string) => Promise<ChatMessage>;
  moveToOnboard: () => void;
  moveToChat: () => void;
}

const createTimeLabel = () =>
  new Date()
    .toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s/g, '');

const createConversationDateLabel = () => new Date().toISOString();

const defaultChatContextValue: ChatContextValue = {
  chatStatus: ChatStatus.ONBOARDING,
  modalType: ChatModalType.NONE,
  chatMode: ChatMode.TIPS,
  conversationId: null,
  conversationStartedAt: null,
  caremapprAppUserId: null,
  onboardingInput: '',
  initialPrompt: null,
  preappointmentQuestionnaireSummary: null,
  chatStartedWithConsultId: false,
  patientConfirmationDetails: null,
  messages: [],
  isTyping: false,
  isStartingConversation: false,
  messageFeedback: {},
  suggestedResponses: [],
  setModalType: () => {},
  setChatStatus: () => {},
  setChatMode: () => {},
  setOnboardingInput: () => {},
  setInitialPrompt: () => {},
  setCaremapprAppUserId: () => {},
  setPreappointmentQuestionnaireSummary: () => {},
  setChatStartedWithConsultId: () => {},
  setPatientConfirmationDetails: () => {},
  setMessages: () => {},
  setIsTyping: () => {},
  setMessageFeedback: () => {},
  setSuggestedResponses: () => {},
  addMessage: () => {},
  startConversation: async () => null,
  sendMessage: async () => ({
    id: '',
    role: 'human',
    text: '',
    createdAt: '',
    data: {},
  }),
  moveToOnboard: () => {},
  moveToChat: () => {},
};

const ChatContext = createContext<ChatContextValue>(defaultChatContextValue);

export const useChatContext = () => useContext(ChatContext);

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [chatStatus, setChatStatus] = useState<ChatStatus>(
    ChatStatus.ONBOARDING,
  );
  const [modalType, setModalType] = useState<ChatModalType>(ChatModalType.NONE);
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.TIPS);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationStartedAt, setConversationStartedAt] = useState<
    string | null
  >(null);
  const [caremapprAppUserId, setCaremapprAppUserId] = useState<string | null>(
    null,
  );
  const [onboardingInput, setOnboardingInput] = useState('');
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
  const [
    preappointmentQuestionnaireSummary,
    setPreappointmentQuestionnaireSummary,
  ] = useState<string | null>(null);
  const [chatStartedWithConsultId, setChatStartedWithConsultId] =
    useState<boolean>(false);
  const [patientConfirmationDetails, setPatientConfirmationDetails] =
    useState<PatientConfirmationDetails | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [messageFeedback, setMessageFeedbackState] = useState<
    Record<string, ChatMessageFeedbackState | undefined>
  >({});
  const [suggestedResponses, setSuggestedResponses] = useState<
    SuggestionResponse[]
  >([]);

  const addMessage = (message: ChatMessage) => {
    setMessages((currentMessages) => [...currentMessages, message]);
  };

  const createHumanMessage = (
    message: string,
    nextConversationId?: string | null,
  ): ChatMessage => ({
    id: `human-${Date.now()}`,
    role: 'human',
    text: message.trim(),
    createdAt: createTimeLabel(),
    data: nextConversationId
      ? {
          conversationId: nextConversationId,
        }
      : {},
  });

  const setMessageFeedback = (
    messageId: string,
    feedback?: ChatMessageFeedback,
  ) => {
    if (!feedback) {
      return;
    }

    setMessageFeedbackState((currentFeedback) => {
      const currentMessageFeedback = currentFeedback[messageId] ?? {};
      const nextValue = !currentMessageFeedback[feedback];
      const nextMessageFeedback: ChatMessageFeedbackState = {
        ...currentMessageFeedback,
        [feedback]: nextValue,
      };

      const hasThumbsUp = Boolean(
        nextMessageFeedback[ChatMessageFeedback.THUMBS_UP],
      );
      const hasThumbsDown = Boolean(
        nextMessageFeedback[ChatMessageFeedback.THUMBS_DOWN],
      );

      return {
        ...currentFeedback,
        [messageId]:
          hasThumbsUp || hasThumbsDown ? nextMessageFeedback : undefined,
      };
    });
  };

  const mapSuggestionResponse = (suggestions: string[]): SuggestionResponse[] =>
    suggestions.map((text, index) => ({
      id: `suggestion-${Date.now()}-${index}`,
      text,
    }));

  const sendMessageWithConversationId = async (
    nextConversationId: string,
    message: string,
    options?: {
      includeHumanMessage?: boolean;
    },
  ) => {
    const trimmedMessage = message.trim();
    const includeHumanMessage =
      typeof options?.includeHumanMessage === 'boolean'
        ? options?.includeHumanMessage
        : true;
    const nextMessage = createHumanMessage(trimmedMessage, nextConversationId);

    if (includeHumanMessage) {
      addMessage(nextMessage);
    }
    setChatMode(ChatMode.NONE);
    setIsTyping(true);

    try {
      const response = await sendChatMessage({
        conversationId: nextConversationId,
        message: trimmedMessage,
        chatbotName: ChatbotName.ENDOZONE_CONSULTATION_SUPPORT,
      });
      const nextSuggestedResponses = mapSuggestionResponse(
        extractSuggestionResponse(response),
      );
      const botMessage: ChatMessage = {
        id: `bot-${Date.now()}`,
        role: 'assistant',
        text: response.message,
        createdAt: createTimeLabel(),
        data: response.data,
      };

      setSuggestedResponses(nextSuggestedResponses);
      setChatMode(
        nextSuggestedResponses.length ? ChatMode.TIPS : ChatMode.NONE,
      );
      addMessage(botMessage);
    } finally {
      setIsTyping(false);
    }

    return nextMessage;
  };

  const startConversation = async () => {
    const pendingInitialPrompt = initialPrompt?.trim();
    setInitialPrompt(null);

    if (pendingInitialPrompt) {
      addMessage(createHumanMessage(pendingInitialPrompt));
    }

    setChatMode(ChatMode.NONE);
    setIsStartingConversation(true);
    setIsTyping(true);

    let nextConversationId: string | null = null;
    try {
      nextConversationId = await createChatSession({
        chatbotName: ChatbotName.ENDOZONE_CONSULTATION_SUPPORT,
        inputs: {
          preappointmentQuestionnaireSummary:
            preappointmentQuestionnaireSummary ?? '',
        },
      });
    } catch (error) {
      setIsStartingConversation(false);
      setIsTyping(false);
      throw error;
    }

    if (!nextConversationId) {
      setIsStartingConversation(false);
      setIsTyping(false);
      return null;
    }

    setConversationId(nextConversationId);
    setConversationStartedAt(createConversationDateLabel());
    setIsStartingConversation(false);

    if (pendingInitialPrompt) {
      void sendMessageWithConversationId(
        nextConversationId,
        pendingInitialPrompt,
        {
          includeHumanMessage: false,
        },
      );
    } else if (preappointmentQuestionnaireSummary) {
      void sendMessageWithConversationId(nextConversationId, 'hi', {
        includeHumanMessage: false,
      });
    }

    return nextConversationId;
  };

  const sendMessage = async (message: string) => {
    if (!conversationId) {
      throw new Error('Conversation has not been started');
    }

    return sendMessageWithConversationId(conversationId, message);
  };

  const moveToOnboard = () => {
    setChatStatus(ChatStatus.ONBOARDING);
    setModalType(ChatModalType.NONE);
    setChatMode(ChatMode.TIPS);
    setConversationId(null);
    setConversationStartedAt(null);
    setCaremapprAppUserId(null);
    setOnboardingInput('');
    setInitialPrompt(null);
    setPreappointmentQuestionnaireSummary(null);
    setChatStartedWithConsultId(false);
    setPatientConfirmationDetails(null);
    setMessages([]);
    setIsStartingConversation(false);
    setIsTyping(false);
    setMessageFeedbackState({});
    setSuggestedResponses([]);
  };

  const moveToChat = () => {
    setChatStatus(ChatStatus.CHAT);
    setModalType(ChatModalType.NONE);
  };
  return (
    <ChatContext.Provider
      value={{
        chatStatus,
        modalType,
        chatMode,
        conversationId,
        conversationStartedAt,
        caremapprAppUserId,
        onboardingInput,
        initialPrompt,
        preappointmentQuestionnaireSummary,
        chatStartedWithConsultId,
        patientConfirmationDetails,
        messages,
        isTyping,
        isStartingConversation,
        messageFeedback,
        suggestedResponses,
        setChatStatus,
        setModalType,
        setChatMode,
        setOnboardingInput,
        setInitialPrompt,
        setCaremapprAppUserId,
        setPreappointmentQuestionnaireSummary,
        setChatStartedWithConsultId,
        setPatientConfirmationDetails,
        setMessages,
        setIsTyping,
        setMessageFeedback,
        setSuggestedResponses,
        addMessage,
        startConversation,
        sendMessage,
        moveToOnboard,
        moveToChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;
